<template>
    <div class="reservations">
<!--        <pre>{{ serie }}</pre>-->

        <h2>{{ heading }}</h2>

        <h3>{{ serie.title }}</h3>
        <h4>{{ serie.dateRangeFormatted }}</h4>

        <div v-if="ajax.loading">
            Loading...
        </div>

        <div class="reservationComplete" v-else-if="['confirmed', 'reserved'].includes(view)">
            <p>
                Your reservation is complete, and your confirmation code is
                <span class="confCode">{{ confCode }}</span>.
            </p>
            <table style="padding-left: 20px;">
                <tr
                    v-for="cost in serie.costs"
                    v-if="cost.n_people > 0"
                >
                    <td v-html="cost.title + ':'"
                        style="text-align: right;"
                    />
                    <td v-text="cost.n_people"
                        style="padding-left: 1em;"
                    />
                </tr>
            </table>
        </div>

        <div class="reservationComplete" v-else-if="view === 'waitlist'">
            <p>
                Your party of {{ n_people }} {{ n_people == 1 ? 'person' : 'people' }}
                has been put on the wait list, and your confirmation code is {{ confCode }}.
                We will contact you if space opens up.
            </p>
        </div>

        <div class="reservationForm" v-else>
            <!-- FROM APP; WANT FOR ASC? -->
            <ul style="margin-bottom: 20px;">
                <li>{{ serie.dateRangeFormatted }}</li>
                <li v-for="cost in serie.costs" v-if="view === 'form'">
                    {{ cost.title || 'Cost per person' }}:
                    <span v-if="cost.cost == 0">Free</span>
                    <span v-else>${{ cost.cost }}</span>
                </li>
            </ul>

            <reservation-contact
                :contact="contact"
                @validity="contactValid = $event"
                @update="updateContact"
                ref="contact"
            />

            <reservation-party
                :party="party"
                :spots_available="serie.spots_available"
                :max_people="serie.max_people"
                :costs="serie.costs"
                @update="updateParty"
                @validity="partyValid = $event"
                @size-status="partySizeStatus = $event"
                ref="party"
            />

            <reservation-payment-test
                v-if="isTestPayment"
                v-show="amountDue > 0"
                :amountDueMessage="amountDueMessage"
                @validity="paymentValid = $event"
                ref="payment"
            />
            <reservation-payment
                v-else
                v-show="amountDue > 0"
                :amountDueMessage="amountDueMessage"
                :stripeKey="stripeKey"
                @validity="paymentValid = $event"
                ref="payment"
            />

            <fieldset style="border: none; padding-top: 0; margin-top: 0; width: auto;">
                <my-input>
                    <template v-slot:custom>
                        <button
                            :disabled="ajax.pending"
                            @click="doSubmit"
                            v-text="buttonText"
                        />
                        <span class="statusMessage" v-if="ajax.pending">{{ ajax.message }}</span>
                        <span
                            class="statusMessage help is-danger"
                            v-if="!ajax.pending && $v.$dirty && $v.$invalid"
                        >
                            Please correct form errors and try again.
                        </span>
                    </template>
                </my-input>
            </fieldset>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import swal from 'sweetalert';
import MyInput from '../../_components/form/MyInput.vue';
import { validationMixin } from 'vuelidate';

import ReservationContact from './Reservation-Contact';
import ReservationParty from './Reservation-Party';
import { PARTY_SIZE_STATUS } from './Reservation-Party';
import ReservationPayment from './Reservation-Payment';
import ReservationPaymentTest from './Reservation-Payment-Test';
import ReservationMembership from "./Reservation-Membership";
import {integer, maxValue, minValue, required, requiredIf} from "vuelidate/lib/validators";

const truthy = (value) => !!value;
// const partyOK = (value) => (value === PARTY_SIZE_STATUS.OK || value === PARTY_SIZE_STATUS.WAIT_LIST);
const paymentOK = (value, vm) => vm.amountDue <= 0 || vm.paymentValid;

export default {
    components: {
        ReservationMembership,
        ReservationContact,
        ReservationParty,
        ReservationPayment,
        ReservationPaymentTest,
        MyInput,
    },
    mixins: [ validationMixin ],
    props: {
        stripeKey: {
            type: String,
        }
    },
    data() {
        return {
            serie: {},

            contact: {},
            contactValid: false,

            party: {
                // n_people: null,
                costs: [],
                names: '',
                contact_notes: '',
                notification_method: null,
            },
            partyValid: false,
            partySizeStatus: null,

            paymentValid: false,

            view: 'form',
            confCode: '',

            ajax: {
                loading: true,
                pending: false,
                message: '',
            }
        }
    },
    validations: {
        contactValid: { truthy },
        partyValid: { truthy },
        paymentValid: { paymentOK },
        // partySizeStatus: { partyOK },
    },
    computed: {
        heading() {
            switch (this.view) {
                case 'confirmed':
                    return 'Reservation complete';
                case 'waitlist':
                    return 'Wait listed';
                default:
                    return 'Reserve your space';
            }
        },
        n_people() {
            if (!this.party.costs) {
                return 0;
            }
            // TODO: Sum only those that contribute to sum
            return this.party.costs.reduce((sum, cost) => sum + (Number(cost.n_people) || 0), 0);
        },
        amountDue() {
            // return this.serie.cost
            //     ? (this.serie.cost * this.party.n_people || 0)
            //     : 0
            // ;
            if (!this.serie.costs) {
                return 0;
            }
            return this.serie.costs.reduce((due, cost) => due + ((Number(cost.n_people) || 0) * cost.cost), 0);
        },
        amountDueMessage() {
            switch(this.partySizeStatus) {
                case PARTY_SIZE_STATUS.OK:
                    return '$' + this.amountDue;
                case PARTY_SIZE_STATUS.WAIT_LIST:
                    return '$' + this.amountDue + ' will be due if space opens up.<br>'
                        + 'Card information is required to join the waitlist.<br>'
                        + 'We will only charge your card if a space opens up '
                        + '<u>and</u> you confirm your attendance.'
                    ;
            }

            return '(Enter valid number of people)';
        },
        buttonText() {
            return this.partySizeStatus === PARTY_SIZE_STATUS.WAIT_LIST ? 'Join waitlist' : 'Reserve now';
        },
        isTestPayment() {
            return /^pk_test_/.test(this.stripeKey);
        },
    },

    methods: {
        updateContact($event) {
            this.contact = Object.assign({}, this.contact, $event);
        },
        updateParty($event) {
            this.party = Object.assign({}, this.party, $event);
        },
        loadData() {
            this.ajax.loading = true;

            const pathParts = window.location.pathname.split('/');
            const hashid = pathParts.pop();

            return axios.get(process.env.MIX_API_BASE_URL + '/api/front/series/' + hashid)
                .then(response => {
                    let serie = response.data;
                    // console.debug(serie);
                    serie.costs.forEach(cost => {
                        cost.n_people = null;
                        return cost;
                    });
                    this.party.costs.splice(0, this.party.costs.length, ...serie.costs);

                    this.serie = Object.assign({}, serie);
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.ajax.loading = false;
                })
            ;
        },
        doSubmit() {
            if (this.$v.$invalid) {
                this.$v.$touch();
                this.$refs.contact.touch();
                this.$refs.party.touch();
                this.$refs.membership && this.$refs.membership.touch();
                this.$refs.payment.touch();
                return;
            }

            let vue = this;

            let paymentPromise;
            this.ajax.pending = true;

            if (this.amountDue > 0) {
                this.ajax.message = 'Contacting payment processor...';
                paymentPromise = this.$refs.payment.getToken()
                    .then(function (result) {
                        if (result.error) {
                            console.error(result.error);
                            axios.post(process.env.MIX_API_BASE_URL + '/log', {message: 'Problem creating token'});
                            axios.post(process.env.MIX_API_BASE_URL + '/log', {data: btoa(JSON.stringify(result.error))});
                            return null;
                        }
                        return result.token.id;
                    })
                    .catch(function (error) {
                        console.error(error);
                        axios.post(process.env.MIX_API_BASE_URL + '/log', {message: 'Problem creating token.'});
                    })
                ;
            } else {
                paymentPromise = Promise.resolve(null);
            }

            paymentPromise
                .then(function(stripeToken) {
                    vue.ajax.message = 'Saving reservation details...';
                    axios.post(process.env.MIX_API_BASE_URL + '/reservations', {
                        serie_hashid: vue.serie.hashid,
                        contact: vue.contact,
                        party: vue.party,
                        stripeToken: stripeToken,
                        isMember: false,
                        // stripeLiveMode: stripeLiveMode,
                        // 'h-captcha-response': document.querySelector('textarea[name="h-captcha-response"]').value,
                    })
                        .then(function (response) {
                            vue.confCode = response.data.confirmation;
                            vue.view = response.data.status;
                        })
                        .catch(function (error) {
                            console.error(error);
                            let message = (error.response && error.response.data && error.response.data.message)
                                ? error.response.data.message
                                : 'Server error.'
                            ;
                            axios.post(process.env.MIX_API_BASE_URL + '/log', {message: 'Problem processing reservation request.'});
                            axios.post(process.env.MIX_API_BASE_URL + '/log', {data: btoa(JSON.stringify({
                                status: (error.response && error.response.status) || 'null',
                                message: message,
                            }))});
                            swal('Oops', 'There was a problem processing your reservation. Please contact us by email or phone.', 'error');
                        })
                        .finally(function() {
                            vue.ajax.pending = false;
                            window.scrollTo(0, 0);
                        })
                    ;
                })
            ;
        },
    },
    created() {
        this.loadData();
    },
}
</script>

<style lang="scss">
.reservations {
    margin: 20px 0 100px 30px;

    @media(max-width: 1000px) {
        margin-left: auto;
        margin-right: auto;
    }

    h2 {
        font-size: 40px;
        margin: 40px 0 30px 0;

        @media(max-width: 1000px) {
            width: 100% !important;
        }
    }
    h3 {
        font-size: 32px;
        margin-bottom: 10px;
    }
    h4 {
        text-align: center;
    }
    & > ul {
        font-size: 24px;
        line-height: 1.7;
        margin-left: 15px;
    }

    p.help {
        margin: 5px 0 0 0;
        line-height: 1;
    }

    .reservationComplete p {
        margin-top: 50px;
        font-size: 24px;
        line-height: 1.4;
    }

    .numberInput {
        width: 5em !important;
    }

    .reservationForm {
        margin: 20px auto 0 auto;

        @media(max-width: 1000px) {
            & > * {
                margin-left: auto;
                margin-right: auto;
            }
        }

        .field {
            @media(max-width: 1000px) {
                flex-direction: column;
            }
        }

        .field-label {
            @media(max-width: 1000px) {
                flex: 0 0 auto;
                width: 100%;
            }
        }

        .input, textarea {
            width: 30em;

            @media(max-width: 1000px) {
                width: 100%;
                max-width: 30em;
            }
        }

        fieldset {
            border: 1px solid #ebdaad;
            border-radius: 10px;
            margin-bottom: 20px;
            display: block;
            width: 30em;
            padding: 20px 40px;

            @media(max-width: 1000px) {
                padding: 10px;
                width: 100%;
                max-width: 30em;
            }
        }

        .field-label {
            width: 9em;
            
            @media(max-width: 1000px) {
                flex: 0 0 auto;
                width: 100%;
            }

            label.label {
                @media(max-width: 1000px) {
                    font-size: 1rem;
                }
            }
        }

        .help {
            font-size: 11pt;
            font-weight: normal;
            line-height: 1.2;

            &.is-danger {
                color: #d8842e;
            }

            &.is-info {
                color: #1a6e74;
            }
        }

        .party p {
            @media(max-width: 1000px) {
                font-size: 1rem;
                margin-top: 0;
            }
        }

        .costs-table th {
            @media(max-width: 1000px) {
                padding-bottom: 10px;
            }
        }

        .party-cost-row {
            @media(max-width: 1000px) {
                display: flex;
                flex-wrap: wrap;

                td {
                    padding-bottom: 0;
                }

                td:nth-child(1), td:nth-child(4) {
                    flex-basis: 100%;
                }

                p.help.is-info {
                    font-size: 0.9rem;
                    padding-bottom: 5px;
                }
            }
        }
    }

    .statusMessage {
        display: inline-block;
        margin-left: 20px;
    }
}
</style>
